import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  SALES_ORDER_TRANSITIONS,
  salesOrdersApi,
  type SalesOrderStatus
} from '@/api/modules/salesOrders';
import { ApiException } from '@/api/types';
import { paths } from '@/router/paths';

const STATUS_COLORS: Record<SalesOrderStatus, 'default' | 'primary' | 'info' | 'success' | 'error' | 'warning'> = {
  DRAFT: 'default',
  CONFIRMED: 'primary',
  IN_PRODUCTION: 'info',
  SHIPPED: 'warning',
  DELIVERED: 'success',
  CANCELLED: 'error'
};

const TRANSITION_LABELS: Record<SalesOrderStatus, string> = {
  DRAFT: 'Move to draft',
  CONFIRMED: 'Confirm order',
  IN_PRODUCTION: 'Send to production',
  SHIPPED: 'Mark shipped',
  DELIVERED: 'Mark delivered',
  CANCELLED: 'Cancel order'
};

export function SalesOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['sales-order', id],
    queryFn: () => salesOrdersApi.get(id!),
    enabled: Boolean(id)
  });

  const transition = useMutation({
    mutationFn: (next: SalesOrderStatus) => salesOrdersApi.transition(id!, next),
    onSuccess: (updated) => {
      queryClient.setQueryData(['sales-order', id], updated);
      queryClient.invalidateQueries({ queryKey: ['sales-orders', 'summaries'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  });

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.error || !query.data) {
    return (
      <Box>
        <BackToList />
        <Alert severity="error">
          Failed to load sales order:{' '}
          {query.error instanceof Error ? query.error.message : 'Not found'}
        </Alert>
      </Box>
    );
  }

  const so = query.data;
  const allowed = SALES_ORDER_TRANSITIONS[so.status];
  const transitionErr =
    transition.error instanceof ApiException
      ? transition.error.error.message
      : transition.error instanceof Error
        ? transition.error.message
        : null;

  return (
    <Box>
      <BackToList />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h1" gutterBottom>
            {so.code}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Created {formatDateTime(so.createdAt)}
          </Typography>
        </Box>
        <Chip
          label={so.status.replace('_', ' ')}
          color={STATUS_COLORS[so.status]}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
        />
      </Stack>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={1.5} divider={<Divider flexItem />}>
            <DetailRow label="Customer">
              <Stack>
                <Typography>{so.customerName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {so.customerCode}
                </Typography>
              </Stack>
            </DetailRow>
            <DetailRow label="Total">
              <Typography variant="h3">
                {so.currency} {so.totalAmount}
              </Typography>
            </DetailRow>
            <DetailRow label="From quotation">
              {so.quotationId ? (
                <Link
                  component="button"
                  type="button"
                  onClick={() =>
                    navigate(paths.quotationDetail.replace(':id', so.quotationId!))
                  }
                >
                  View source quotation
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  (Standalone — not linked to a quotation)
                </Typography>
              )}
            </DetailRow>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h2" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 2 }}>
        Actions
      </Typography>

      {allowed.length === 0 ? (
        <Alert severity="info">
          This order is in a terminal state — no further transitions are allowed.
        </Alert>
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
              {allowed.map((next) => (
                <Button
                  key={next}
                  variant={next === allowed[0] ? 'contained' : 'outlined'}
                  color={next === 'CANCELLED' ? 'error' : 'primary'}
                  onClick={() => transition.mutate(next)}
                  disabled={transition.isPending}
                >
                  {TRANSITION_LABELS[next]}
                </Button>
              ))}
            </Stack>
            {transitionErr && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {transitionErr}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function BackToList() {
  const navigate = useNavigate();
  return (
    <Button
      startIcon={<ArrowBackIcon />}
      size="small"
      onClick={() => navigate(paths.salesOrders)}
      sx={{ mb: 2 }}
    >
      Back to sales orders
    </Button>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Stack>
  );
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
}
